import type { Workspace } from '@core/services/auth/workspace.service.types';

/**
 * Extracted from ToolbarWorkspaceSwitcherComponent — this is now the single
 * source. The workspace-switch curtain consumes the same palette/hash so a
 * tile's colour matches what the user just clicked on in the palette.
 */
export const WORKSPACE_PALETTE_COLORS: readonly string[] = [
  '#3b82f6',
  '#8b5cf6',
  '#ec4899',
  '#f59e0b',
  '#10b981',
  '#06b6d4',
  '#6366f1',
  '#ef4444'
];

export function getWorkspaceInitials(
  workspace: Pick<Workspace, 'name'> | undefined
): string {
  if (!workspace) return '?';
  const name = workspace.name || '';
  const words = name.trim().split(/\s+/);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  if (words.length === 1 && words[0].length >= 2)
    return words[0].substring(0, 2).toUpperCase();
  if (words.length === 1 && words[0].length === 1)
    return words[0][0].toUpperCase();
  return '?';
}

export function getWorkspaceColor(
  workspace: Pick<Workspace, 'name' | 'color'> | undefined
): string {
  if (!workspace) return '#64748b';
  if (workspace.color) return workspace.color;
  const hash = workspace.name
    .split('')
    .reduce((acc: number, char: string) => acc + char.charCodeAt(0), 0);
  return WORKSPACE_PALETTE_COLORS[hash % WORKSPACE_PALETTE_COLORS.length];
}

/** A single tile in the workspace-switch rail — what WorkspaceSwitchMarker.rail carries. */
export interface WorkspaceSwitchRailEntry {
  id: string;
  name: string;
  initials: string;
  color: string;
}

/**
 * Caps `orderedRows` to `maxEntries`, centred on the destination (4 before /
 * target / 4 after by default), sliding the window inward when the
 * destination sits near either edge. Order is preserved as received — the
 * caller passes the exact order the user saw in the palette at click time.
 * Returns [] when `destinationId` isn't found (defensive — should not happen
 * in practice, since the destination is always a row the user just clicked).
 */
export function buildWorkspaceSwitchRail(
  orderedRows: readonly WorkspaceSwitchRailEntry[],
  destinationId: string,
  maxEntries = 9
): WorkspaceSwitchRailEntry[] {
  const idx = orderedRows.findIndex((r) => r.id === destinationId);
  if (idx === -1) return [];
  if (orderedRows.length <= maxEntries) return [...orderedRows];

  const before = Math.floor((maxEntries - 1) / 2);
  const after = maxEntries - 1 - before;
  let start = idx - before;
  let end = idx + after;
  if (start < 0) {
    end += -start;
    start = 0;
  }
  if (end > orderedRows.length - 1) {
    start -= end - (orderedRows.length - 1);
    end = orderedRows.length - 1;
  }
  start = Math.max(0, start);
  return orderedRows.slice(start, end + 1);
}
